'use client';

import { Button } from '@/components/ui/button';
import type { LocationForecast } from '@/lib/forecast';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface ForecastDaySelectorProps {
  forecastDates: string[];
  forecastsByDate: Record<string, LocationForecast | null>;
  selectedDayIndex: number;
  onSelectDay: (index: number) => void;
  onPrevious: () => void;
  onNext: () => void;
  canGoPrevious: boolean;
  canGoNext: boolean;
}

export function ForecastDaySelector({
  forecastDates,
  forecastsByDate,
  selectedDayIndex,
  onSelectDay,
  onPrevious,
  onNext,
  canGoPrevious,
  canGoNext,
}: ForecastDaySelectorProps) {
  return (
    <nav className="flex items-center justify-between" aria-label="7-day forecast navigation">
      <Button
        variant="ghost"
        size="sm"
        onClick={onPrevious}
        disabled={!canGoPrevious}
        className="h-8 w-8 p-0"
        aria-label="Previous day"
      >
        <ChevronLeft className="h-4 w-4" aria-hidden="true" />
      </Button>
      <div className="flex gap-1 overflow-x-auto py-1 px-2">
        {forecastDates.map((date, index) => {
          const dateObj = new Date(date + 'T12:00:00');
          const dayName = index === 0 ? 'Today' : index === 1 ? 'Tomorrow' : dateObj.toLocaleDateString('en-US', { weekday: 'short' });
          const dayNum = dateObj.getDate();
          const isSelected = selectedDayIndex === index;
          const dayForecast = forecastsByDate[date];
          const bestQuality = dayForecast ? Math.max(dayForecast.sunriseQuality, dayForecast.sunsetQuality) : 0;

          return (
            <button
              key={date}
              onClick={() => onSelectDay(index)}
              className={`flex flex-col items-center min-w-[52px] px-2 py-1.5 rounded-lg transition-all ${
                isSelected
                  ? 'bg-amber-500 text-white shadow-md'
                  : 'hover:bg-amber-100 dark:hover:bg-amber-900/30'
              }`}
              aria-label={`${dayName}, ${dateObj.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}${dayForecast ? `, best quality ${bestQuality}%` : ''}`}
              aria-pressed={isSelected}
            >
              <span className="text-xs font-medium">{dayName}</span>
              <span className={`text-lg font-bold ${isSelected ? '' : 'text-foreground'}`}>{dayNum}</span>
              {dayForecast && (
                <span className={`text-xs ${isSelected ? 'text-white/80' : 'text-muted-foreground'}`}>
                  {bestQuality}%
                </span>
              )}
            </button>
          );
        })}
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={onNext}
        disabled={!canGoNext}
        className="h-8 w-8 p-0"
        aria-label="Next day"
      >
        <ChevronRight className="h-4 w-4" aria-hidden="true" />
      </Button>
    </nav>
  );
}
